import { ChangeDetectionStrategy, Component, Input } from '@angular/core'
import { range } from '@engineering11/utility'
import { FontConfig, ICoreConfig, IDimensions, ISvgScales, SvgNode } from '../core/culture-index-chart.model'
import { transitionOpacity } from '../utils/d3-utils'
import { CultureIndexTraitSymbol } from '../../core/culture-index.model'

type DrawFrameArgs = ISvgScales & IDimensions & FontConfig
@Component({
  selector: 'culture-index-chart-frame',
  standalone: true,
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CultureIndexChartFrameComponent {
  @Input({ required: true }) coreConfig!: ICoreConfig
  @Input({ required: true }) euText!: string

  /**
   * Draws the Frame, the Axises, and the Text for the axises
   */
  drawFrame(svg: SvgNode, args: DrawFrameArgs, loading?: boolean) {
    const { xScale, yScale, width, height, fontSize } = args
    svg
      .append('rect')
      .attr('class', 'frame')
      .attr('x', this.coreConfig.margin.left + xScale(1))
      .attr('y', this.coreConfig.margin.top)
      .attr('width', width - this.coreConfig.margin.left - xScale(1) - this.coreConfig.margin.right)
      .attr('height', height - this.coreConfig.margin.top - this.coreConfig.margin.bottom)
      .attr('stroke', () => (loading ? '#e0e0e0' : '#000'))
      .attr('stroke-width', 2.5)
      .attr('fill', 'none')

    // Add grid container
    const gridGroup = svg.append('g')
    // Add separator line
    gridGroup
      .append('line')
      .attr('class', 'frame')
      .attr('x1', this.coreConfig.margin.left + xScale(1))
      // lower it more .5 to make it look like more as the ui design
      .attr('y1', yScale(5 + 0.5))
      .attr('x2', width - this.coreConfig.margin.right)
      .attr('y2', yScale(5 + 0.5))
      .attr('stroke', '#000')
      .attr('stroke', () => (loading ? '#e0e0e0' : '#000'))
      .attr('stroke-width', 2.5)

    // Add X axis labels (top)
    const topAxisGroup = gridGroup.append('g')
    topAxisGroup
      .selectAll('text')
      .data(range(11))
      .enter()
      .append('text')
      .attr('class', 'frame')
      .attr('x', d => xScale(d) + xScale(2))
      .attr('y', this.coreConfig.margin.top + 5 + fontSize)
      .attr('text-anchor', 'middle')
      .attr('font-size', `${fontSize - 2}px`)
      .attr('font-weight', 'bold')
      .attr('fill', () => (loading ? '#e0e0e0' : '#000'))
      .call(transitionOpacity, 500, 200)
      .text(d => d)

    // Add X axis labels (bottom)
    const bottomAxisGroup = gridGroup.append('g')
    bottomAxisGroup
      .selectAll('text')
      .data(range(11))
      .enter()
      .append('text')
      .attr('class', 'frame')
      .attr('x', d => xScale(d) + xScale(2))
      .attr('y', yScale(5 + 0.5) - 10)
      .attr('text-anchor', 'middle')
      .attr('font-size', `${fontSize - 2}px`)
      .attr('font-weight', 'bold')
      .attr('fill', () => (loading ? '#e0e0e0' : '#000'))
      .call(transitionOpacity, 500, 200)
      .text(d => d)

    // Add Y axis labels
    const yAxisGroup = gridGroup.append('g')
    yAxisGroup
      .selectAll('text')
      .data(Object.values(CultureIndexTraitSymbol))
      .enter()
      .append('text')
      .attr('class', 'frame')
      .attr('x', this.coreConfig.margin.left - 15 + xScale(1))
      .attr('y', (_, i) => yScale(i < 4 ? i + 1 + 0.25 : i + 2 + 0.25))
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .attr('font-size', `${fontSize}px`)
      .attr('font-weight', 'bold')
      .attr('fill', () => (loading ? '#e0e0e0' : '#000'))
      .call(transitionOpacity, 500, 200)
      .text(d => d)

    // Add 'EU = 41 - 49' label - fixed positioning to match design
    yAxisGroup
      .append('text')
      .attr('class', 'frame')
      .attr('x', this.coreConfig.margin.left - (this.euText.length * 2 + 9) + xScale(1))
      .attr('y', yScale(5 + 0.5) - 8)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .attr('fill', () => (loading ? '#e0e0e0' : '#000'))
      .call(transitionOpacity, 500, 200)
      .text(this.euText)
  }

  /**
   * Updates the Frame, the Axises, and the Text for the axises
   */
  updateFrame(svg: SvgNode, loading?: boolean) {
    svg.selectAll('text.frame').attr('fill', () => (loading ? '#e0e0e0' : '#000'))
    svg.selectAll('rect.frame, line.frame').attr('stroke', () => (loading ? '#e0e0e0' : '#000'))
  }
}
